<template>
    <div class="entity-tabs">
        <button :class="{'active': value === 'main'}" @click="$emit('input', 'main')">
            <i class="far fa-fw" :class="mainIcon"/>{{mainLabel}}
        </button>
        <!-- Only rendered where the host card passes a contact count, so cards
             without contacts keep the original three-tab strip. -->
        <button v-if="showContacts" :class="{'active': value === 'contacts'}" @click="$emit('input', 'contacts')">
            <i class="far fa-fw fa-address-book"/>Contacts
            <b-tag class="ml-1" v-if="contactCount > 0">{{contactCount}}</b-tag>
        </button>
        <button :class="{'active': value === 'tasks'}" @click="$emit('input', 'tasks')">
            <i class="far fa-fw fa-tasks"/>Tasks
            <b-tag :type="taskCount > 0 ? 'is-warning' : ''" class="ml-1" v-if="taskCount > 0 || taskCompletedCount > 0">{{taskCompletedCount}} / {{taskCount + taskCompletedCount}}</b-tag>
        </button>
        <button :class="{'active': value === 'notes'}" @click="$emit('input', 'notes')">
            <i class="far fa-fw fa-sticky-note"/>Notes
            <b-tag class="ml-1" v-if="noteCount > 0">{{noteCount}}</b-tag>
        </button>
    </div>
</template>

<script>
    export default {
        name: 'EntityTabs',
        props: {
            // Active tab: 'main' | 'contacts' | 'tasks' | 'notes'. Use with v-model.
            value: {
                type: String,
                default: 'main'
            },
            // Contacts is opt-in per card -- entities whose "contact" is the
            // traveller themselves (customers, leads, per-registration records)
            // don't carry the tab. Pass :contact-count to switch it on.
            showContacts: {
                type: Boolean,
                default: false
            },
            contactCount: {
                type: Number,
                default: 0
            },
            mainLabel: {
                type: String,
                default: 'Details'
            },
            mainIcon: {
                type: String,
                default: 'fa-align-left'
            },
            taskCount: {
                type: Number,
                default: 0
            },
            // Completed-task count -- the badge reads "completed / total",
            // amber while anything is still open.
            taskCompletedCount: {
                type: Number,
                default: 0
            },
            noteCount: {
                type: Number,
                default: 0
            }
        }
    };
</script>

<style lang="scss" scoped>
    // Inverse of CardToolbar's chrome: the strip is white and the ACTIVE tab
    // takes the toolbar grey, so it reads as attached to the grey toolbar
    // that sits directly beneath it on most cards (including the panes' own
    // toolbars).
    .entity-tabs {
        background: white;
        border-bottom: 1px solid #dee2e6;
        // Real headroom above the tabs so they read as tabs.
        padding: 0.45rem 0.4rem 0 0.6rem;
        // The card is a fixed-height flex column: with content overflowing it
        // SHRINKS auto-height children, which squeezed this strip on the Main
        // tab (more chrome visible) and let it pop back taller on the other
        // tabs -- the strip visibly jumped on every switch. Never shrink.
        flex-shrink: 0;
        // Flex instead of inline flow: inline layout reserves descender space
        // under the buttons, leaving a white sliver between the active tab
        // and the toolbar. Bottom-aligned flex lets the -1px margin actually
        // sit on the border.
        display: flex;
        align-items: flex-end;

        button {
            padding: 0.2rem 0.5rem;
            line-height: normal;
            font-size: 0.82rem;
            // Every tab is the same fixed height, sized to fit a full-size
            // Bulma tag -- so badges appearing/disappearing (and tab
            // switching) never change the strip's geometry.
            display: inline-flex;
            align-items: center;
            height: 2rem;
            // Constant weight -- swapping weight on the active tab shifts the
            // whole strip's layout.
            font-weight: 700;

            background-color: transparent;
            // Every state keeps the same box: a transparent border swapped for
            // a colored one, so activating a tab never shifts the strip.
            border: 1px solid transparent;
            margin-bottom: -1px;
            color: darken($t-primary, 10);
            border-radius: 0.2rem 0.2rem 0 0;
            margin-right: 0.2rem;
            cursor: pointer;

            i {
                color: $t-primary;
                margin-right: 0.4rem;
            }

            // The selected tab is not a button to press -- it keeps its
            // resting look under the cursor instead of inverting.
            &:hover:not(.active) {
                background: $t-primary;
                color: white !important;

                i {
                    color: white !important;
                }
            }

            &.active {
                background: #f3f3f3;
                border-color: #dee2e6;
                border-bottom-color: #f3f3f3;
            }

            &:focus {
                outline-color: $t-primary;
            }

            // Neutral count badges (Notes; Tasks once everything is complete)
            // flip to white on the ACTIVE tab, where Bulma's default grey
            // tag vanished against the grey background. Inactive tabs are
            // white, so the grey default already reads there.
            &.active .tag:not(.is-warning) {
                background: white;
            }
        }

    }
</style>
